/**
 * ============================================================================
 * OBJECTS STORE - MIGRATION OPERATIONS
 * ============================================================================
 * 
 * Handles migrating objects to new blueprint versions.
 * 
 * Migration is used when a blueprint is updated and existing objects
 * need to be updated to match the new structure.
 * 
 * @module objects/api/migrate
 */

import { apiService } from "@/lib/api-functions/api-service";
import { objectsStore } from "../store";
import type { ObjectItem, ToastFunction } from "../types";
import type { Version } from "../../storeTypes";

// ============================================================================
// MIGRATION OPERATIONS
// ============================================================================

/**
 * Migrate an object to a new blueprint version.
 * 
 * Use this when a blueprint has been updated and you need to update
 * existing objects to match the new structure.
 * 
 * @param objectId - The object ID to migrate
 * @param targetVersion - The target blueprint version to migrate to
 * @param strategy - Migration strategy: 'merge' (preserve existing data) or 'overwrite' (use blueprint defaults)
 * @param toast - Optional toast function for error reporting
 * @returns Promise resolving to the migrated object
 * 
 * @example
 * ```ts
 * // Migrate with merge strategy (default)
 * const migrated = await migrateObject(
 *   'object-123',
 *   { major: 2, minor: 0, patch: 0 }
 * );
 * 
 * // Migrate with overwrite strategy
 * const migrated = await migrateObject(
 *   'object-123',
 *   { major: 2, minor: 0, patch: 0 },
 *   'overwrite'
 * );
 * ```
 */
export async function migrateObject(
  objectId: string,
  targetVersion: Version,
  strategy: 'merge' | 'overwrite' = 'merge',
  toast?: ToastFunction
): Promise<ObjectItem> {
  const currentData = objectsStore.getData();
  const existingItem = currentData.find(item => item.id === objectId);

  if (!existingItem) {
    throw new Error('Object not found');
  }

  try {
    // Call API to apply migration
    const response = await apiService.migrateObject(objectId, targetVersion, strategy);

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to migrate object');
    }

    const migratedObject = response.data;

    // Update the object in the store
    const updatedData = currentData.map(item =>
      item.id === objectId ? migratedObject : item
    );
    objectsStore.setData(updatedData);

    const versionString = `${targetVersion.major}.${targetVersion.minor}.${targetVersion.patch}`;
    console.log(`[migrateObject] Migrated object ${objectId} to version ${versionString}`);

    if (toast) {
      toast({
        title: 'Success',
        description: `Object successfully migrated to version ${versionString}`,
      });
    }

    return migratedObject;
    
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to migrate object';
    console.error('[migrateObject] Error:', err);
    
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    
    throw new Error(message);
  }
}
